const USER_SCROLL_EVENTS = ["wheel", "touchstart", "pointerdown", "keydown"];
const SETTLE_DELAY_MS = 800;

/**
 * Smooth-scrolls a scroll container to a target offset, robust to virtualized
 * lists: Chrome aborts a native smooth scroll when the content changes mid-way
 * (e.g. a placeholder is prepended and the virtual layout reflows), and a long
 * smooth scroll mounts every card it passes. So: jump instantly to at most
 * `maxSmoothDistance` from the target, smooth-scroll the rest, and snap to the
 * target afterwards if it was interrupted (unless the user scrolled).
 *
 * `getTarget` may be a number or a function, re-evaluated at settle time in case
 * the layout above the target changed. Returns a cancel function.
 */
export const smoothScrollContainerTo = (
  container,
  getTarget = 0,
  { maxSmoothDistance } = {}
) => {
  if (!container) return () => {};

  const resolveTarget = () => {
    const raw = typeof getTarget === "function" ? getTarget() : getTarget;
    const max = container.scrollHeight - container.clientHeight;
    return Math.max(0, Math.min(Number(raw) || 0, Math.max(0, max)));
  };

  const smoothLimit = maxSmoothDistance ?? container.clientHeight;
  const target = resolveTarget();
  const distance = container.scrollTop - target;
  if (Math.abs(distance) > smoothLimit) {
    container.scrollTo({
      top: target + Math.sign(distance) * smoothLimit,
      behavior: "instant",
    });
  }
  container.scrollTo({ top: target, behavior: "smooth" });

  let timeoutId = null;
  const cleanup = () => {
    clearTimeout(timeoutId);
    USER_SCROLL_EVENTS.forEach((eventName) =>
      container.removeEventListener(eventName, cleanup)
    );
  };

  USER_SCROLL_EVENTS.forEach((eventName) =>
    container.addEventListener(eventName, cleanup, { passive: true })
  );

  timeoutId = setTimeout(() => {
    cleanup();
    const finalTarget = resolveTarget();
    if (Math.abs(container.scrollTop - finalTarget) > 1) {
      container.scrollTo({ top: finalTarget, behavior: "instant" });
    }
  }, SETTLE_DELAY_MS);

  return cleanup;
};

/** Offset that centers `element` vertically inside `container`. */
export const getCenteredOffsetInContainer = (container, element) => {
  if (!container || !element) return 0;
  const containerRect = container.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();
  const elementTop = elementRect.top - containerRect.top + container.scrollTop;
  return elementTop - (container.clientHeight - elementRect.height) / 2;
};
